import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    alignment = "alignment",
    label = "label",
    buttonLink = "buttonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.alignment]: string;
        [fields.label]: string;
        [fields.buttonLink]: LinkFieldValue;
    };
}

type Props = OwnProps;

export interface SubscribeStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    ctaButton?: ButtonPresentationProps;
}

export const joinColonyStyles: SubscribeStyles = {
    mainGridContainer: {
        css: css`
            width: 100%;
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 24px 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
            justify-content: center;
        `,
    },
    titleLabel: {
        variant: "h2",
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            justify-content: center;
        `,
    },
    descriptionText: {
        css: css`
            margin-bottom: 0;
        `,
    },
    buttonGridItem: {
        width: 12,
        css: css`
            padding-top: 40px;
            justify-content: center;
        `,
    },
    ctaButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css``,
                        css`
                            width: 160px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

export const JoinColony = ({ fields }: Props) => {
    const styles = joinColonyStyles;
    const { url } = useGetLink(fields.buttonLink);
    const { isInShell } = useShellContext();
    const history = useHistory();

    const handleBtnClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleLabel}>{fields.title}</Typography>
            </GridItem>
            <GridItem {...styles.descriptionGridItem}>
                <Typography {...styles.descriptionText}>{parse(fields.description, parserOptions)}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Button {...styles.ctaButton} onClick={() => handleBtnClick(url)}>
                    {fields.label}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: JoinColony,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
            },
            {
                name: fields.buttonLink,
                fieldType: "Contextual",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
